import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores';
import { APP_NAME } from '@/stores/partner.store';
import { returnPathFrom } from '../lib/utils';

export default function AuthLayout() {
  const { isAuthenticated } = useAuthStore();
  const location = useLocation();

  // If already authenticated, go to the page that sent them here (or the dashboard)
  if (isAuthenticated) {
    return <Navigate to={returnPathFrom(location.state)} replace />;
  }

  return (
    <div className="relative flex min-h-screen overflow-hidden bg-paper">
      {/* Ambient hero glow */}
      <div
        className="pointer-events-none absolute -left-[10%] -top-[20%] h-[720px] w-[80%]"
        style={{
          background:
            'radial-gradient(46% 46% at 18% 30%, rgb(var(--coral) / .14), transparent 70%), radial-gradient(50% 50% at 78% 18%, rgb(var(--brand) / .18), transparent 70%)',
          filter: 'blur(8px)',
        }}
      />
      {/* Left side - Branding */}
      <div className="relative z-10 hidden lg:flex lg:w-1/2 lg:flex-col lg:justify-center lg:px-12">
        <div className="mx-auto max-w-lg">
          <div className="mb-8">
            <div className="mb-6 flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-[11px] bg-gradient-to-br from-brand to-brand-ink shadow-s">
                <svg
                  viewBox="0 0 24 24"
                  className="h-5 w-5"
                  fill="none"
                  stroke="#fff"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M2 12h4l2.5-7 4 14 3-9 2 2h4" />
                </svg>
              </span>
              <span className="font-display text-2xl font-bold tracking-tight text-ink">{APP_NAME}</span>
            </div>
            <h1 className="font-display text-5xl font-bold leading-[1.04] text-ink">
              Every conversation, handled.
            </h1>
            <p className="mt-4 text-xl text-ink-2">
              AI-powered platform for exceptional customer service
            </p>
          </div>

          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-card bg-brand text-white shadow-s">
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-ink">Real-time chat</h3>
                <p className="text-ink-2">
                  Handle customer conversations with instant messaging
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-card bg-coral text-white shadow-s">
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-ink">AI assistant</h3>
                <p className="text-ink-2">
                  Intelligent agent handles common requests automatically
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-card bg-brand-ink text-white shadow-s">
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-ink">Analytics</h3>
                <p className="text-ink-2">
                  Track performance and customer satisfaction metrics
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Auth Form */}
      <div className="relative z-10 flex w-full items-center justify-center px-6 py-12 lg:w-1/2 lg:px-12">
        <Outlet />
      </div>
    </div>
  );
}
